/**
 * Visible Content Registry
 *
 * Tracks visible article tiles for analytics augmentation.
 * Used to decorate NTP analytics events with visible tile data for CTR measurement.
 */

import debug from '../utils/console'
import { calculateStoryAge } from './date/calculateStoryAge'

const logger = debug.withTag('visibleContent')

export interface VisibleContentMetadata {
  headline?: string
  publisher?: string
  url?: string // External article URL
  summary_url?: string // OneNews summary page URL
  category?: string
  type?: 'article' | 'video' | 'game'
  publishedDate?: string | Date
}

interface VisibleContentEntry {
  id: string
  sortOrder: number
  isVisible: boolean
  metadata?: VisibleContentMetadata
}

/**
 * Registry of content items. Entries persist across unmount/remount cycles
 * for resize stability. They're only cleared on "load more" events.
 */
const visibleContentRegistry = new Map<string, VisibleContentEntry>()
let sortCounter = 0

/**
 * Register a content item for visibility tracking.
 * Returns the sort order assigned to this item.
 * Note: Re-registering a previously unregistered ID returns the same sortOrder (for resize stability).
 */
export function registerVisibleContent(
  id: string,
  metadata?: VisibleContentMetadata
): number {
  if (!id) return -1

  // If already registered (even if previously unmounted), update metadata and return existing sortOrder
  const existing = visibleContentRegistry.get(id)
  if (existing) {
    if (metadata) {
      existing.metadata = metadata
    }
    return existing.sortOrder
  }

  const sortOrder = sortCounter++
  visibleContentRegistry.set(id, {
    id,
    sortOrder,
    isVisible: false, // Initially not visible until observer confirms
    metadata,
  })

  return sortOrder
}

/**
 * Update the visibility state of a registered content item.
 */
export function setVisibleContent(id: string, isVisible: boolean): void {
  const entry = visibleContentRegistry.get(id)
  if (entry) {
    entry.isVisible = isVisible
  }
}

/**
 * Unregister a content item from visibility tracking.
 * Entry is kept in registry for resize stability (sortOrder preservation).
 */
export function unregisterVisibleContent(id: string): void {
  const entry = visibleContentRegistry.get(id)
  if (entry) {
    entry.isVisible = false
  }
}

/**
 * Get all currently visible content items, sorted by their mount order.
 * Returns array with id, sortOrder, and metadata.
 */
export function getVisibleContent(includeOnlyVisible: boolean = true): Array<{
  id: string
  sortOrder: number
  headline?: string
  publisher?: string
  url?: string
  summary_url?: string
  category?: string
  type?: 'article' | 'video' | 'game'
  story_age?: number
  position_row?: number
  position_col?: number
}> {
  const items = Array.from(visibleContentRegistry.values())
    .filter((item) => !includeOnlyVisible || item.isVisible)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  const cappedResults = items.slice(0, 100)
  if (items.length > 100) {
    logger.warn(`Content exceeds 100 items (${items.length}), capping to 100`)
  }

  return cappedResults.map(({ id, sortOrder, metadata }) => ({
    id,
    sortOrder,
    headline: metadata?.headline,
    publisher: metadata?.publisher,
    url: metadata?.url,
    summary_url: metadata?.summary_url,
    category: metadata?.category,
    type: metadata?.type,
    story_age: metadata?.publishedDate
      ? calculateStoryAge(metadata.publishedDate)
      : undefined,
    // position_row and position_col will be calculated in helper.ts where column_count is available
  }))
}

/**
 * Check if any content has been registered.
 * Useful for determining if ArticleCards have mounted and registered.
 */
export function hasRegisteredContent(): boolean {
  return visibleContentRegistry.size > 0
}

/**
 * Clear all registered content items.
 * Called after "load more" events to reset sortOrders to 0.
 */
export function clearVisibleContent(): void {
  visibleContentRegistry.clear()
  sortCounter = 0
}
